import { UseQueryOptions } from '@tanstack/react-query';
import { useMemo } from 'react';
import { ONE_HOUR } from '@/util/time';
import { useBusinessEntityQuery } from '../businessEntity/queries';
import { formatDeliveryPointAddress } from './address';
import useBusinessRelations from './useBusinessRelations';

export interface DeliveryPointModel extends DeliveryPointDto {
  mdgErpAccountId?: string;
}

const useDeliveryPointModels = (
  deliveryPoints?: DeliveryPointDto[]
): DeliveryPointModel[] | undefined => {
  const { businessRelations } = useBusinessRelations();

  return useMemo(
    () =>
      deliveryPoints?.map((deliveryPoint) => ({
        ...deliveryPoint,
        mdgErpAccountId: businessRelations.find(
          (businessRelation) =>
            businessRelation.id === deliveryPoint.parentIds?.at(0)
        )?.mdgErpAccountId,
      })),
    [deliveryPoints, businessRelations]
  );
};

export const useDeliveryPointsQuery = (
  queryOptions: Partial<UseQueryOptions<DeliveryPointDto[]>> = {}
) => {
  const deliveryPointsQuery = useBusinessEntityQuery<DeliveryPointDto[]>(
    {
      type: 'mdg_delivery_point',
    },
    {
      include: ['parents', 'permits'],
    },
    {
      gcTime: ONE_HOUR,
      ...queryOptions,
    }
  );
  const deliveryPoints = useDeliveryPointModels(deliveryPointsQuery.data);

  return { ...deliveryPointsQuery, data: deliveryPoints };
};

const useDeliveryPoints = () => {
  const { data = [], ...rest } = useDeliveryPointsQuery();

  const deliveryPoints = data.filter(
    (d) => d.permits?.includes('ecom.price:r') && d.mdgStatus === 'A'
  );
  const options: DeliveryPointOption[] = deliveryPoints
    .filter((d) => d.permits?.includes('ecom.order:w'))
    .map((deliveryPoint) => ({
      id: deliveryPoint.id,
      title: formatDeliveryPointAddress(deliveryPoint),
    }))
    .sort((a, b) => a.title.localeCompare(b.title));

  return { deliveryPoints, options, ...rest };
};

export default useDeliveryPoints;

export const useDeliveryPoint = (deliveryPointId?: string) => {
  const { data: deliveryPoints = [] } = useDeliveryPointsQuery();

  return deliveryPoints.find(
    (deliveryPoint) => deliveryPoint.id === deliveryPointId
  );
};

export const useDeliveryPointsByCompany = (companyId: string) => {
  const deliveryPointsQuery = useBusinessEntityQuery<DeliveryPointDto[]>(
    {
      type: 'mdg_delivery_point',
      ancestor: companyId,
    },
    {
      include: ['parents', 'permits'],
      size: 1000,
    },
    {
      gcTime: ONE_HOUR,
    }
  );
  const deliveryPoints = useDeliveryPointModels(deliveryPointsQuery.data) ?? [];

  return { ...deliveryPointsQuery, deliveryPoints };
};
